import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  VStack,
  HStack,
  Spinner,
  useColorModeValue
} from "@chakra-ui/react";
import axios from "axios";
import Loading from "../resources/Loading";

export default function FetchChat({ id, toData, to }) {
  const authPicture = localStorage.getItem("authpicture");
  const authName = localStorage.getItem("authname");
  const authemail = localStorage.getItem("authemail");
  const authToken = localStorage.getItem("authToken");
  const [newMessages, setNewMessages] = useState([]);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const messageContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Move useColorModeValue hooks outside of the render function
  const incomingMessageBg = useColorModeValue("gray.100", "gray.700");
  const incomingMessageColor = useColorModeValue("gray.800", "white");

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() &&
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  useEffect(() => {
    if (id) {
      const fetchNewMessages = async () => {
        const currentMessageLength = newMessages.length;
        try {
          const response = await axios.get("https://api.agromanch.com/api/new-messages", {
            params: { id, to },
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });
          const data = response.data;
          setNewMessages(data);
          setIsLoading(false);

          if (currentMessageLength !== data.length) {
            setHasNewMessages(true);
          } else {
            setHasNewMessages(false);
          }
        } catch (error) {
          console.error(error);
          setIsLoading(false);
        }
      };

      // Fetch new messages every second
      const intervalId = setInterval(fetchNewMessages, 1000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [id, authToken, newMessages]);

  useEffect(() => {
    if (hasNewMessages) {
      scrollToBottom();
      setHasNewMessages(false);
    }
  }, [hasNewMessages]);

  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };
  
  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100%" minH="200px">
        <Spinner color="green.500" size="lg" />
      </Flex>
    );
  }

  return (
    <VStack
      spacing={4}
      align="stretch"
      ref={messageContainerRef}
      overflowY="auto"
      maxH="60vh"
      px={2}
    >
      {newMessages.map((message, index) => (
        <Box key={index} w="100%">
          {message.from === authemail ? (
            // Outgoing message (from current user)
            <Flex justify="flex-end">
              <Box maxW="70%">
                <HStack spacing={2} justify="flex-end" mb={1}>
                  <Text fontSize="xs" color="gray.500">
                    {formatTime(message.createdAt)}
                  </Text>
                  <Text fontSize="xs" fontWeight="medium">
                    {authName}
                  </Text>
                </HStack>
                
                <Flex>
                  <Box
                    bg="green.500"
                    color="white"
                    borderRadius="lg"
                    borderTopRightRadius="0"
                    px={4}
                    py={2}
                    boxShadow="sm"
                  >
                    <Text fontSize="sm">{message.message}</Text>
                  </Box>
                  <Avatar
                    src={authPicture}
                    size="sm"
                    ml={2}
                    border="2px solid white"
                    boxShadow="sm"
                  />
                </Flex>
              </Box>
            </Flex>
          ) : (
            // Incoming message (from other user)
            <Flex justify="flex-start">
              <Box maxW="70%">
                <HStack spacing={2} mb={1}>
                  <Text fontSize="xs" fontWeight="medium">
                    {toData.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {formatTime(message.createdAt)}
                  </Text>
                </HStack>
                
                <Flex>
                  <Avatar
                    src={toData.picture}
                    size="sm"
                    mr={2}
                    border="2px solid white"
                    boxShadow="sm"
                  />
                  <Box
                    bg={incomingMessageBg}
                    color={incomingMessageColor}
                    borderRadius="lg"
                    borderTopLeftRadius="0"
                    px={4}
                    py={2}
                    boxShadow="sm"
                  >
                    <Text fontSize="sm">{message.message}</Text>
                  </Box>
                </Flex>
              </Box>
            </Flex>
          )}
        </Box>
      ))}
      
      {newMessages.length === 0 && (
        <Flex 
          justify="center" 
          align="center" 
          h="100%" 
          minH="200px"
          direction="column"
        >
          <Text color="gray.500" fontSize="sm">No messages yet</Text>
          <Text color="gray.400" fontSize="xs" mt={1}>Start the conversation!</Text>
        </Flex>
      )}
    </VStack>
  );
}
